import { timeoutDefaultValue } from '@gik/core/constants';
import { useBemCN } from '@gik/core/utils/bemBlock';
import React from 'react';
import type { Color, ColorResult } from 'react-color';
import useLatest from 'react-use/lib/useLatest';
import type { BaseColorPickerProps } from './components/BaseColorPicker';
import { BaseColorPicker } from './components/BaseColorPicker';

export type ColorPickerProps = {
  value?: Color;
  defaultColor?: Color;
  onChange?: (color: Color) => void;
} & BaseColorPickerProps;

export function ColorPicker({ value, defaultColor = '#000000', onChange, onSubmit, ...otherProps }: ColorPickerProps) {
  const bem = useBemCN('solid-color-picker');

  const [_color, setColor] = React.useState<Color>(value || defaultColor);
  const latestColor = useLatest(_color);
  const [shouldRerender, setShouldRerender] = React.useState<Boolean>(false);

  function rerender() {
    setShouldRerender(true);
    setTimeout(() => {
      setShouldRerender(false);
    }, timeoutDefaultValue);
  }

  React.useEffect(() => {
    if (value !== latestColor.current) {
      setColor(value || defaultColor);
      rerender();
    }
  }, [value, defaultColor, latestColor]);

  const handleChange = React.useCallback(
    (colorResult: ColorResult) => {
      setColor(colorResult?.hex);
      onChange?.(colorResult?.hex);
    },
    [onChange]
  );

  if (shouldRerender) return <div {...bem('rerender')} />;

  return <BaseColorPicker {...otherProps} color={_color} onChange={handleChange} onSubmit={onSubmit} />;
}
